import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DataGrid, GridColDef, GridColumnHeaders, GridRowId } from "@mui/x-data-grid"
import { useTranslation } from "react-i18next"

import Icon from "@/components/icon/Icon"
import { colorTokens } from "@/components/colorTokens"
import { TableProps } from "@/types/table"
import { buildPinnedSx, hasLeftPins } from "./stickyPinning"

import "./table.scss"

const UNIT_TABLE_COLUMN_HEIGHT = 58
const SELECTION_TABLE_COLUMN_HEIGHT = 44
const DEFAULT_TABLE_COLUMN_HEIGHT = 40

// Community master/detail replacement: a synthetic detail row is injected after
// each expanded row; its first cell colSpans the full width to render renderDetails.
const DETAIL_PREFIX = "__detail__::"
const DETAIL_TOGGLE_FIELD = "__detail_panel_toggle__"

type DetailRow = { id: string; __detailParent: Record<string, unknown> }

const isDetailRow = (row: unknown): row is DetailRow =>
  typeof (row as { id?: unknown })?.id === "string" &&
  (row as { id: string }).id.startsWith(DETAIL_PREFIX)

const TableSortedIconDescend = () => <Icon type="arrow-line-down" size={14} />
const TableSortedIconAscend = () => <Icon type="arrow-line-up" size={14} />

const TableNoRowsOverlay = () => {
  const { t } = useTranslation()

  return (
    <div className="custom-no-rows-overlay">
      <p>{t("messages.noDataAvailable")}</p>
    </div>
  )
}

export default function Table({
  renderDetails,
  columns,
  oddRowColor,
  ref,
  hasSummaryRow = false,
  fillLastColumn = false,
  pinnedColumns,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  getRowId,
  getRowHeight,
  onRowClick,
  ...props
}: TableProps & { collapsedId?: number }) {
  const [stateColumns, setStateColumns] = useState<GridColDef[]>(columns)
  const [internalExpanded, setInternalExpanded] = useState<Set<GridRowId>>(new Set())
  const localRef = useRef<HTMLDivElement>(null)
  const columnHeaderRef = useRef<HTMLDivElement>(null)

  const tableRef = ref || localRef
  const expanded = detailPanelExpandedRowIds ?? internalExpanded

  const gridIdOf = useCallback(
    (row: Record<string, unknown>): GridRowId =>
      getRowId ? getRowId(row) : (row as { id: GridRowId }).id,
    [getRowId],
  )

  const toggleDetail = useCallback(
    (id: GridRowId) => {
      const next = new Set(expanded)

      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }

      if (onDetailPanelExpandedRowIdsChange) {
        onDetailPanelExpandedRowIdsChange(next)
      } else {
        setInternalExpanded(next)
      }
    },
    [expanded, onDetailPanelExpandedRowIdsChange],
  )

  useEffect(() => {
    if (!columns || columns.length === 0) {
      setStateColumns([])

      return
    }

    const processedColumns: GridColDef[] = columns.map((col, index) => {
      if (index === columns.length - 1) {
        return {
          ...col,
          resizable: false,
          // 남는 폭을 마지막 컬럼이 채움 — flex 지정 시 기존 width 는 최소폭으로 승격 (WEB-2218)
          ...(fillLastColumn ? { flex: 1, minWidth: col.width ?? col.minWidth } : {}),
        }
      }

      return col
    })

    if (renderDetails) {
      const totalCols = processedColumns.length + 1
      const [first, ...rest] = processedColumns

      // The first column carries the full-width detail content via colSpan.
      const firstWithDetail: GridColDef = {
        ...first,
        colSpan: (_value, row) => (isDetailRow(row) ? totalCols : 1),
        renderCell: (params) => {
          if (isDetailRow(params.row)) {
            return (
              <div className="detail-panel-content" style={{ width: "100%" }}>
                {renderDetails(params.row.__detailParent)}
              </div>
            )
          }

          return first.renderCell
            ? first.renderCell(params)
            : ((params.formattedValue ?? params.value) as ReactNode)
        },
      }

      // Trailing toggle column shows the expand/collapse affordance (toggling
      // itself happens on row click, matching the previous Pro behaviour).
      const toggleColumn: GridColDef = {
        field: DETAIL_TOGGLE_FIELD,
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        align: "right",
        renderCell: (params) =>
          isDetailRow(params.row) ? null : (
            <Icon
              type={expanded.has(params.id) ? "arrow-up" : "arrow-down"}
              color={colorTokens.icon.enabled}
              size={20}
            />
          ),
      }

      setStateColumns([firstWithDetail, ...rest, toggleColumn])
    } else {
      setStateColumns(processedColumns)
    }
  }, [columns, renderDetails, expanded, fillLastColumn])

  const rows = useMemo(() => {
    const base = props.rows ?? []

    if (!renderDetails) {
      return base
    }

    const out: Record<string, unknown>[] = []

    for (const row of base) {
      out.push(row)

      const gid = gridIdOf(row)

      if (expanded.has(gid)) {
        out.push({ id: `${DETAIL_PREFIX}${gid}`, __detailParent: row })
      }
    }

    return out
  }, [props.rows, renderDetails, expanded, gridIdOf])

  const columnHeaderHeight = useMemo(() => {
    if (stateColumns.some((column: GridColDef & { unit?: boolean }) => !!column.unit)) {
      return UNIT_TABLE_COLUMN_HEIGHT
    }
    if (props.columnHeaderHeight) {
      return props.columnHeaderHeight
    }
    if (props.selectionType) {
      return SELECTION_TABLE_COLUMN_HEIGHT
    }

    return DEFAULT_TABLE_COLUMN_HEIGHT
  }, [props.columnHeaderHeight, props.selectionType, stateColumns])

  useEffect(() => {
    const resize = () => {
      window.dispatchEvent(new Event("resize"))
    }

    resize()
  }, [stateColumns, rows])

  const pinnedSx = useMemo(
    () => buildPinnedSx(stateColumns, pinnedColumns),
    [stateColumns, pinnedColumns],
  )

  return (
    <DataGrid
      {...props}
      rows={rows}
      columns={stateColumns}
      ref={tableRef}
      getRowId={renderDetails ? (row) => (isDetailRow(row) ? row.id : gridIdOf(row)) : getRowId}
      onRowClick={(params, event, details) => {
        if (isDetailRow(params.row)) {
          return
        }

        if (renderDetails) {
          toggleDetail(params.id)
        }

        onRowClick?.(params, event, details)
      }}
      getRowHeight={(rowParams) => {
        if (isDetailRow(rowParams.model)) {
          return "auto"
        }

        return getRowHeight ? getRowHeight(rowParams) : null
      }}
      onColumnWidthChange={(params, event, details) => {
        // 고정 컬럼은 sticky 오프셋을 정적 width로 계산하므로, 리사이즈된 width를
        // stateColumns 에 반영해 오프셋을 다시 계산해야 컬럼이 어긋나지 않는다.
        if (hasLeftPins(pinnedColumns)) {
          setStateColumns((prev) => {
            const target = prev.find((c) => c.field === params.colDef.field)

            if (!target || target.width === params.width) {
              return prev
            }

            return prev.map((c) =>
              c.field === params.colDef.field ? { ...c, width: params.width } : c,
            )
          })
        }

        props.onColumnWidthChange?.(params, event, details)
      }}
      disableVirtualization={hasLeftPins(pinnedColumns) || props.disableVirtualization}
      columnBufferPx={0}
      scrollEndThreshold={0}
      checkboxSelection={!!props.selectionType}
      disableMultipleRowSelection={!!props.selectionType && props.selectionType !== "checkbox"}
      slots={{
        pagination: props.slots?.pagination,
        ...props.slots,
        columnHeaders: (headerProps) => {
          if (headerProps?.ref) {
            columnHeaderRef.current = (headerProps.ref as RefObject<HTMLDivElement>).current
          }

          return <GridColumnHeaders {...headerProps} />
        },
        columnSortedDescendingIcon: TableSortedIconDescend,
        columnSortedAscendingIcon: TableSortedIconAscend,
        noRowsOverlay: TableNoRowsOverlay,
        baseCheckbox: props.selectionElement,
      }}
      sx={{
        ...props.sx,
        ...pinnedSx,
        ".MuiDataGrid-columnHeader--withRightBorder": {
          borderRight: "none !important",
        },
        ".MuiDataGrid-cell--withRightBorder": {
          borderRight: "none !important",
        },
        ".MuiDataGrid-columnHeader--withRightBorder .MuiDataGrid-columnSeparator": {
          opacity: 1,
        },
        ".MuiDataGrid-columnSeparator--sideRight": {
          right: 0,
        },
        "--DataGrid-t-color-interactive-selected": "transparent !important",
      }}
      slotProps={{
        ...props.slotProps,
        row: {
          "table-ref": tableRef,
          "column-header-ref": columnHeaderRef,
        } as Record<string, any>,
      }}
      disableColumnMenu
      rowHeight={props.rowHeight || 44}
      columnHeaderHeight={columnHeaderHeight}
      getRowClassName={(params) => {
        let className = ""

        if (isDetailRow(params.row)) {
          return "detail-row"
        }

        if (hasSummaryRow && oddRowColor) {
          if (params.indexRelativeToCurrentPage % 2 === 0) {
            className += "odd-row "
          }
        } else if (oddRowColor) {
          if (params.indexRelativeToCurrentPage % 2 === 1) {
            className += "odd-row "
          }
        }
        if (params.row.isDisabled) {
          className += "disabled-row "
        }
        if (params.row.isBold) {
          className += "bold-row "
        }
        if (renderDetails) {
          className += "expandable-row "
        }

        return className
      }}
    />
  )
}
